import React, { memo, useMemo, useRef } from "react";
import { useImmer } from "use-immer";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  CustomEventDto,
} from "@intelligems/ig-types";
import { GraphConfig } from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { InsufficientCogsAlert } from "@src/features/analytics/baseDashboard/InsufficientCogsAlert";
import { NoCogsGraphPlaceholder } from "@src/features/analytics/baseDashboard/NoCogsGraphPlaceholder";
import { useSetGridWidthAnalytics } from "@src/features/analytics/hooks/use-set-grid-width-analytics";
import { MetricDetailsWrapper } from "@src/features/analytics/metricDetails/MetricDetailsWrapper";
import { AnalyticsAudienceTableWrapper } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTableWrapper";
import {
  getGridWidthCalc,
  shouldShowInsufficientCogsWarning,
  shouldShowProfitData,
} from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

interface SubscriptionAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}
export const SubscriptionAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: SubscriptionAnalyticsPageProps) => {
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");
    const shouldShowProfit = useMemo(() => {
      return shouldShowProfitData(data[0]?.data?.metrics || []);
    }, [data]);

    const [hover, setHover] = useImmer(-1);
    const ref = useRef<HTMLDivElement | null>(null);
    const gridWidth = useSetGridWidthAnalytics(ref);

    return (
      <Stack ref={ref} direction="column" spacing={4}>
        {shouldShowInsufficientCogsWarning(data) && <InsufficientCogsAlert />}
        <Grid>
          <BaseMetricSection groupTitle={"Subscription Metrics"}>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(0);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  totalTestVisitors={totalExperienceVisitors}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  testDuration={testDuration}
                  config={graphConfigs[0]}
                  renderLocation={"experiment"}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[0],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(1);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  config={graphConfigs[1]}
                  totalTestVisitors={totalExperienceVisitors}
                  renderLocation={"experiment"}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[1],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              {!shouldShowProfit ? (
                <NoCogsGraphPlaceholder config={graphConfigs[2]} />
              ) : (
                <BaseGraphPaper
                  onMouseEnter={() => {
                    setHover(2);
                  }}
                  onMouseLeave={() => {
                    setHover(-1);
                  }}
                >
                  <GraphsWrapper
                    testDuration={testDuration}
                    isDataLoading={data[0].isLoading || data[0].isPending}
                    config={graphConfigs[2]}
                    renderLocation={"experiment"}
                    totalTestVisitors={totalExperienceVisitors}
                    data={selectData({
                      dashboardData: data,
                      config: graphConfigs[2],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    rawData={data}
                    compareVariationId={compareVariationId}
                  />
                </BaseGraphPaper>
              )}
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(3);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  config={graphConfigs[3]}
                  renderLocation={"experiment"}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[3],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(4);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  config={graphConfigs[4]}
                  renderLocation={"experiment"}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[4],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(5);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  config={graphConfigs[5]}
                  renderLocation={"experiment"}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[5],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(6);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  config={graphConfigs[6]}
                  renderLocation={"experiment"}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[6],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
          </BaseMetricSection>
        </Grid>
        <Grid>
          <MetricDetailsWrapper
            graphConfigs={[
              graphConfigs[0],
              graphConfigs[1],
              graphConfigs[2],
              graphConfigs[3],
              graphConfigs[5],
              graphConfigs[6],
              graphConfigs[7],
            ]}
            dropdownOptions={[
              graphConfigs[0],
              graphConfigs[1],
              graphConfigs[2],
              graphConfigs[3],
              graphConfigs[5],
              graphConfigs[6],
              graphConfigs[7],
            ].map((config) => ({
              title: config.title[0],
              id: config.metrics[0],
            }))}
            variationOverviewData={data}
          />
        </Grid>
        <Grid>
          <AnalyticsAudienceTableWrapper
            testDuration={testDuration}
            experience={experience}
            variations={variations}
            dashboardData={data}
            graphConfigs={graphConfigs.filter((config) => config.hasAudience)}
          />
        </Grid>
      </Stack>
    );
  }
);
